import type { ComponentPropsWithoutRef, ReactNode } from 'react';



import { cn } from '@/lib/utils';



type CliDocSpanProps = ComponentPropsWithoutRef<'span'> & {

  'data-cli'?: string;

  'data-variant'?: string;

  dataCli?: string;

  dataVariant?: string;

};



/** Wire id -> readable label (matches settings CliArgs humanize). */

function humanizeChoiceLabel(raw: string): string {

  const trimmed = raw.replace(/^-+/, '').trim();

  if (!trimmed) return raw;

  return trimmed

    .split(/[-_]/)

    .filter(Boolean)

    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())

    .join(' ');

}



function resolveCliKind(props: CliDocSpanProps): string | undefined {

  return props['data-cli'] ?? props.dataCli;

}



function resolveCliVariant(props: CliDocSpanProps): string | undefined {

  return props['data-variant'] ?? props.dataVariant;

}



const tagVariantClass: Record<string, string> = {

  flag: 'border-emerald-500/35 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',

  flags: 'border-sky-500/35 bg-sky-500/10 text-sky-700 dark:text-sky-300',

  enum: 'border-violet-500/35 bg-violet-500/10 text-violet-700 dark:text-violet-300',

  required: 'border-amber-500/35 bg-amber-500/10 text-amber-800 dark:text-amber-200',

  optional: 'border-border/70 bg-muted/30 text-muted-foreground',

};



/**

 * Styled spans for Tanit CLI reference markdown (`data-cli` from cli_doc_markdown.hpp).

 * Plain markdown viewers ignore the attributes; MarkdownRenderer maps them to chips.

 */

export function CliDocSpan({ children, className, ...rest }: CliDocSpanProps) {

  const kind = resolveCliKind(rest);

  const variant = resolveCliVariant(rest);



  if (kind === 'meta') {

    return (

      <span

        className={cn(

          'inline-flex flex-wrap items-center gap-x-1.5 gap-y-1 align-middle not-prose',

          className,

        )}

        {...rest}

      >

        {children as ReactNode}

      </span>

    );

  }



  if (kind === 'type') {

    return (

      <span

        className={cn(

          'inline-flex items-center rounded px-1.5 py-0.5 font-mono text-[0.72rem] leading-none',

          'border border-border/70 bg-muted/40 text-muted-foreground not-prose',

          className,

        )}

        {...rest}

      >

        {children}

      </span>

    );

  }



  if (kind === 'tag') {

    const palette = (variant && tagVariantClass[variant]) || 'border-border/70 bg-muted/30 text-muted-foreground';

    return (

      <span

        className={cn(

          'inline-flex items-center rounded-full px-2 py-0.5 text-[0.68rem] font-semibold uppercase tracking-wide',

          'border not-prose',

          palette,

          className,

        )}

        {...rest}

      >

        {children}

      </span>

    );

  }



  if (kind === 'choice') {

    const raw = String(children ?? '').trim();

    const label = humanizeChoiceLabel(raw);

    return (

      <span

        title={raw}

        className={cn(

          'inline-flex items-center rounded-md px-1.5 py-0.5 text-[0.72rem] leading-none',

          'border border-primary/25 bg-primary/10 text-primary not-prose',

          className,

        )}

        {...rest}

      >

        {label}

      </span>

    );

  }



  if (kind === 'choices') {

    return (

      <span

        className={cn(

          'inline-grid grid-cols-2 gap-1 align-middle not-prose sm:grid-cols-[repeat(2,minmax(0,max-content))]',

          className,

        )}

        {...rest}

      >

        {children as ReactNode}

      </span>

    );

  }



  if (kind === 'default') {

    return (

      <span className={cn('inline-flex flex-wrap items-center gap-1 text-muted-foreground not-prose', className)} {...rest}>

        {children}

      </span>

    );

  }



  if (kind === 'value') {

    return (

      <code className={cn('rounded bg-muted/50 px-1 py-0.5 font-mono text-[0.72rem] not-prose', className)} {...rest}>

        {children}

      </code>

    );

  }



  return (

    <span className={className} {...rest}>

      {children}

    </span>

  );

}


